/**
 * Craft Beer BOGO Methodology Reminder (AD-4177)
 *
 * When an unbranded Craft Beer product is advertised with a BOGO offer at one
 * of the retailers below, entry people should be reminded to pick the product
 * from the text rather than the branded product(s) in the image.
 *
 * Detection is done entirely client-side at offer approval:
 *   - Craft Beer product : product.sub_cat_i (or product.brand.sub_cat_i) equals
 *                          the "Craft Beer" sub-category ext_id (250349).
 *   - BOGO offer         : offer.offer_type is "bogo" (case-insensitive).
 *   - Retailer           : the block's retailer name is in CRAFT_BEER_BOGO_RETAILERS.
 *
 * The reminder is advisory: the entry person can acknowledge it and continue.
 */

// product_categories.ext_id where sub_cat_nm = "Craft Beer"
// (brands join to categories via BrandModel.sub_cat_i == ProductCategoryModel.ext_id)
export const CRAFT_BEER_SUB_CAT_I = 250349;

// Retailer names (exact) where the reminder applies.
export const CRAFT_BEER_BOGO_RETAILERS = [
  "Publix Super Markets",
  "Winn Dixie",
  "Meijer",
  "Safeway NOC",
  "Target Stores",
  "Hy-Vee Food Stores",
  "Tops Markets",
  "Nob Hill Foods",
  "Raley's",
  "Lucky Supermarkets",
  "Bel Air",
  "Save Mart",
  "Sprouts Farmers Market",
];

export const CRAFT_BEER_BOGO_MESSAGE =
  "Please review the selected products to make sure you are using the text to pick the product unless it meets the criteria to use the image, which you can find here: https://sites.google.com/numerator.com/pepbasicmethodology/entering-vaguely-advertised-products\n\nRemember, if a product is called out unbranded in the text, such as just 'Craft Beer', 'Beer', etc., that is not grounds to pick the branded product(s) from the image. You should enter the unbranded product instead.";

const isBogoOffer = (offer) =>
  (offer?.offer_type || "").trim().toLowerCase() === "bogo";

const isCraftBeerProduct = (product) => {
  const subCat = product?.sub_cat_i ?? product?.brand?.sub_cat_i;
  return subCat != null && Number(subCat) === CRAFT_BEER_SUB_CAT_I;
};

/**
 * Returns true when the block should show the Craft Beer BOGO reminder:
 * a Craft Beer product + a BOGO offer at one of the listed retailers.
 */
export const isCraftBeerBogoViolation = ({
  products = [],
  offers = [],
  retailerName = "",
} = {}) =>
  CRAFT_BEER_BOGO_RETAILERS.includes(retailerName) &&
  products.some(isCraftBeerProduct) &&
  offers.some(isBogoOffer);
